/**
 * AddDataMenu — the global "+ Add data" entry point (See the work).
 *
 * One always-visible button opening the four intake paths (registry:
 * lib/addDataConfig): create a ticket, upload meeting notes (Doc → Stories),
 * import a spreadsheet, connect Jira / Azure DevOps. Exists because getting
 * work INTO Avium was navigation-buried — the doc flow especially sat four
 * clicks deep behind "Spreadsheet / File".
 *
 * Two grounds, one component (Law 7 — same pattern as GlobalControls'
 * variant split): v1 renders it in the green Header (`variant="header"`,
 * the default — byte-identical to pre-clean-slate markup). The clean-slate
 * chrome (v2, flag-gated, docs/design/chrome-cleanslate-hifi.html, approved
 * by Brian 2026-07-18) renders it as the sidebar's primary create action
 * (`variant="sidebar"`) — same state, handlers, and registry items, just a
 * button styled for the dark sidebar ground and a menu that opens under it.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADD_DATA_ITEMS, openDocImport, type AddDataItem } from '../lib/addDataConfig';
import { TOUR_ANCHOR } from '../lib/surfaceRegistry';

export type AddDataMenuVariant = 'header' | 'sidebar';

interface Props {
  variant?: AddDataMenuVariant;
}

export default function AddDataMenu({ variant = 'header' }: Props) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const run = (item: AddDataItem) => {
    setOpen(false);
    if (item.action.kind === 'doc-import') openDocImport();
    else navigate(item.action.to);
  };

  const buttonClassName = variant === 'sidebar'
    ? 'w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold bg-brand-primary text-white hover:bg-brand-primary transition-colors ring-1 ring-white/15 hover:ring-white/30'
    : 'flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold bg-card text-brand-primary dark:text-brand-accent hover:bg-teal-50 shadow-sm transition-colors';

  const menuClassName = variant === 'sidebar'
    ? 'absolute left-0 top-full mt-1 bg-card rounded-xl shadow-xl border border-line py-2 w-80 z-50'
    : 'absolute left-0 top-9 bg-card rounded-xl shadow-xl border border-line py-2 w-80 z-50';

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        data-tour={TOUR_ANCHOR.createTicket}
        title="Add data — create, upload, import, or connect"
        className={buttonClassName}
      >
        <span className="text-sm leading-none">＋</span>
        Add data
        <span className="text-[10px]">▾</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className={menuClassName}>
            {ADD_DATA_ITEMS.map(item => (
              <button
                key={item.key}
                onClick={() => run(item)}
                className="w-full text-left px-4 py-2.5 hover:bg-brand-primary-light/60 transition-colors flex items-start gap-3"
              >
                <span className="text-lg leading-none mt-0.5">{item.icon}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink">{item.label}</span>
                  <span className="block text-xs text-ink-faint mt-0.5">{item.description}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
